import React, { useEffect, useState } from 'react';
import { useAppStore } from '../../App';

interface Props {
 onClose: () => void;
 onCreated?: () => void;
}

interface IndustryOption { code: string; ko_name: string; group_name?: string; }
interface RegionOption { sido: string; gugun: string; }

const todayStr = (): string => new Date().toISOString().slice(0, 10);
const plus10YearsStr = (): string => {
 const d = new Date();
 d.setFullYear(d.getFullYear() + 10);
 return d.toISOString().slice(0, 10);
};

/**
 * 슬롯 교환 (바터) 매물 등록 모달.
 * 기본 모드: 1슬롯 + 기간 10년 + 자동 이름 ("${닉네임} 제공 슬롯") + 기본 모패밀리에서 1슬롯 자동 위임
 * 고급 모드 (폴딩): 제목/설명/슬롯수/업종/지역 직접 입력. 자동 위임은 항상 ON.
 */
const BarterRegisterModal: React.FC<Props> = ({ onClose, onCreated }) => {
 const { user } = useAppStore();
 const ownerName = user?.name || user?.email?.split('@')[0] || '나';

 const [advanced, setAdvanced] = useState(false);
 const [title, setTitle] = useState('');
 const [description, setDescription] = useState('');
 const [periodStart, setPeriodStart] = useState('');
 const [periodEnd, setPeriodEnd] = useState('');
 const [totalSlots, setTotalSlots] = useState(1);
 const [industry, setIndustry] = useState('');
 const [sido, setSido] = useState('');
 const [gugun, setGugun] = useState('');
 const [industries, setIndustries] = useState<IndustryOption[]>([]);
 const [regions, setRegions] = useState<RegionOption[]>([]);
 const [error, setError] = useState<string | null>(null);
 const [submitting, setSubmitting] = useState(false);

 useEffect(() => {
  if (advanced) {
   if (industries.length === 0) {
    fetch('/market/listings/meta/industries').then(r => r.json()).then(j => setIndustries(j.data || []));
   }
   if (regions.length === 0) {
    fetch('/market/listings/meta/regions').then(r => r.json()).then(j => setRegions(j.data || []));
   }
  }
 }, [advanced]);

 const sidos = [...new Set(regions.map(r => r.sido))];
 const guguns = sido ? regions.filter(r => r.sido === sido).map(r => r.gugun) : [];

 const submit = async () => {
  // 기본 모드: 자동 값으로 채움. 고급 모드: 사용자 입력값 사용.
  const finalTitle = advanced ? title.trim() : `${ownerName} 제공 슬롯`;
  const finalDescription = advanced ? description : '';
  const finalStart = advanced ? periodStart : todayStr();
  const finalEnd = advanced ? periodEnd : plus10YearsStr();
  const finalSlots = advanced ? totalSlots : 1;

  if (advanced && (!finalTitle || !finalStart || !finalEnd)) {
   setError('제목/기간은 필수입니다.');
   return;
  }

  setSubmitting(true);
  setError(null);
  try {
   const res = await fetch('/market/listings', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
     side: 'barter',
     title: finalTitle,
     description: finalDescription,
     period_start: finalStart,
     period_end: finalEnd,
     total_slots: finalSlots,
     industry_ksic_major: advanced && industry ? industry : undefined,
     region_sido: advanced && sido ? sido : undefined,
     region_gugun: advanced && gugun ? gugun : undefined,
     auto_delegate_from_default: true,
    }),
   });
   const j = await res.json();
   if (!res.ok || !j.success) throw new Error(j.error || '등록 실패');
   onCreated?.();
   onClose();
  } catch (err: any) {
   setError(err.message || '등록 실패');
  } finally {
   setSubmitting(false);
  }
 };

 return (
  <div
   className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
   onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
  >
   <div className="bg-white p-3 md:p-6 max-w-xl w-full max-h-[90vh] overflow-y-auto">
    <div className="flex justify-between items-center mb-3">
     <h2 className="text-lg md:text-xl font-bold">슬롯 교환 매물 등록</h2>
     <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
    </div>

    <div className="bg-purple-50 border border-purple-200 p-2 text-xs text-purple-800 mb-3">
     기본 등록 시 가상 모패밀리 <strong>"{ownerName} 제공 슬롯"</strong> 가 1슬롯으로 만들어지고, 본인 기본 모패밀리의 빈 슬롯 1개를 자동으로 독점 위임받습니다.
     매칭 후엔 짝이 그 슬롯의 영상을 자유롭게 점유/교체합니다.
    </div>

    {!advanced && (
     <div className="text-sm space-y-2 mb-3">
      <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
       <div>이름: <span className="text-gray-900">{ownerName} 제공 슬롯</span></div>
       <div>슬롯: <span className="text-gray-900">1개</span></div>
       <div>기간: <span className="text-gray-900">{todayStr()}</span></div>
       <div>~ <span className="text-gray-900">{plus10YearsStr()}</span> (10년)</div>
      </div>
      <button
       onClick={() => setAdvanced(true)}
       className="text-xs text-blue-600 hover:text-blue-800 underline"
      >
       고급 옵션 ▾ (제목/슬롯수/기간/업종/지역 직접 설정)
      </button>
     </div>
    )}

    {advanced && (
     <div className="space-y-3 text-sm">
      <button
       onClick={() => setAdvanced(false)}
       className="text-xs text-blue-600 hover:text-blue-800 underline mb-1"
      >
       ▴ 기본 모드로 돌아가기
      </button>

      <div>
       <label className="block text-gray-600 mb-1">제목 *</label>
       <input
        type="text"
        value={title}
        onChange={e => setTitle(e.target.value)}
        maxLength={200}
        className="w-full px-2 py-1 border border-gray-300"
        placeholder={`예: ${ownerName} 제공 슬롯`}
       />
      </div>

      <div>
       <label className="block text-gray-600 mb-1">설명</label>
       <textarea
        value={description}
        onChange={e => setDescription(e.target.value)}
        rows={3}
        className="w-full px-2 py-1 border border-gray-300"
       />
      </div>

      <div className="grid grid-cols-2 gap-2">
       <div>
        <label className="block text-gray-600 mb-1">기간 시작 *</label>
        <input type="date" value={periodStart} onChange={e => setPeriodStart(e.target.value)} className="w-full px-2 py-1 border border-gray-300" />
       </div>
       <div>
        <label className="block text-gray-600 mb-1">기간 종료 *</label>
        <input type="date" value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} className="w-full px-2 py-1 border border-gray-300" />
       </div>
      </div>

      <div>
       <label className="block text-gray-600 mb-1">슬롯 수 (기본 모패밀리에서 자동 위임)</label>
       <input
        type="number"
        min="1"
        max="10"
        value={totalSlots}
        onChange={e => setTotalSlots(Math.max(1, Math.min(10, Number(e.target.value) || 1)))}
        className="w-24 px-2 py-1 border border-gray-300"
       />
       <span className="text-xs text-gray-500 ml-2">기본 1개. 최대 10개. 본인 기본 모패밀리에 빈 슬롯이 그 수만큼 있어야 합니다.</span>
      </div>

      <div>
       <label className="block text-gray-600 mb-1">
        매장 분류{' '}
        <span
         className="text-gray-400 cursor-help"
         title={'스크린이 설치된 장소의 업종 (또는 광고주의 사업 분류). 광고주·미디어 매칭 정확도를 높이기 위해 대분류로 선택.'}
        >ⓘ</span>
       </label>
       <select value={industry} onChange={e => setIndustry(e.target.value)} className="w-full px-2 py-1 border border-gray-300">
        <option value="">선택 안 함</option>
        {Object.entries(industries.reduce((acc, i) => {
         const g = i.group_name || '기타';
         (acc[g] ||= []).push(i);
         return acc;
        }, {} as Record<string, typeof industries>)).map(([group, items]) => (
         <optgroup key={group} label={group}>
          {items.map(i => <option key={i.code} value={i.code}>{i.ko_name}</option>)}
         </optgroup>
        ))}
       </select>
      </div>

      <div className="grid grid-cols-2 gap-2">
       <div>
        <label className="block text-gray-600 mb-1">시·도</label>
        <select value={sido} onChange={e => { setSido(e.target.value); setGugun(''); }} className="w-full px-2 py-1 border border-gray-300">
         <option value="">선택 안 함</option>
         {sidos.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
       </div>
       <div>
        <label className="block text-gray-600 mb-1">구·군</label>
        <select value={gugun} onChange={e => setGugun(e.target.value)} disabled={!sido} className="w-full px-2 py-1 border border-gray-300 disabled:opacity-50">
         <option value="">선택 안 함</option>
         {guguns.map(g => <option key={g} value={g}>{g}</option>)}
        </select>
       </div>
      </div>
     </div>
    )}

    {error && <div className="bg-red-50 text-red-700 p-2 text-xs my-3">{error}</div>}

    <div className="flex gap-2 pt-2">
     <button
      onClick={submit}
      disabled={submitting}
      className="flex-1 px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50"
     >
      {submitting ? '등록 중...' : '등록'}
     </button>
     <button
      onClick={onClose}
      className="flex-1 px-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700"
     >
      취소
     </button>
    </div>
   </div>
  </div>
 );
};

export default BarterRegisterModal;
